import React, { useRef, useState } from "react";
import { useChatContext } from "../context";
import MessageList from "./MessageList";
import ChatInput, { ChatInputHandle } from "./ChatInput";
import ChatHeader from "./ChatHeader";
import DebugPanel from "./DebugPanel";
import { getGearClickAction } from "../utils/debugPanelUtils";
import { useImageAttachments } from "../hooks/useImageAttachments";
import { useChatTurn } from "../hooks/useChatTurn";
import { TrainingAPI } from "@api/TrainingAPI";

const trainingAPI = new TrainingAPI();

// ponytail: pacing constants for the "deliberate streaming" prototype. Tune here while evaluating.
// Drip speed ≈ CHARS_PER_TICK / TICK_MS chars per ms. We append a few chars per tick rather than
// one, because one setTimeout per char hits the browser's short-timer clamp and floors the speed.
const CHARS_PER_TICK = 24;
const TICK_MS = 12;
const LABEL_DWELL_MS = 280;  // how long a "Calling X" planning label stays visible

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const ChatInterface: React.FC = () => {
    const {
        conversationEpoch,
        isTyping,
        selectedMessage,
        setSelectedMessage,
        drawerMode,
        setDrawerMode,
    } = useChatContext();

    const { sendMessage } = useChatTurn();
    const { attachments, setAttachments, attachmentsRef, stageFiles, handleRemove } = useImageAttachments(conversationEpoch, isTyping);
    // An answer from a message row goes through the composer, which owns the draft and the send.
    const inputRef = useRef<ChatInputHandle>(null);
    // Only file drags count: the table header reorder inside MessageList is a drag too.
    // dragleave also fires when the drag moves onto a child, so the overlay goes off only when
    // the drag moves to an element outside the zone. A flag, not an enter/leave count: during
    // streaming the list replaces elements, and a removed element sends no dragleave.
    const [dragOver, setDragOver] = useState(false);
    const hasFiles = (e: React.DragEvent) => e.dataTransfer.types.includes("Files");

    const handleGearClick = () => {
        const action = getGearClickAction(drawerMode);
        if (action === "open") {
            setSelectedMessage(null);
            setDrawerMode("conversation");
        } else {
            setDrawerMode(null);
        }
    };

    const handleDrawerClose = () => {
        setDrawerMode(null);
        setSelectedMessage(null);
    };

    const panelTitle = drawerMode === "conversation" ? "Settings" : drawerMode === "message" ? "Tool calls" : "";

    return (
        <div className="chat-v2-interface">
            <ChatHeader
                settingsOpen={drawerMode === "conversation"}
                onSettingsToggle={handleGearClick}
            />
            <div className="chat-v2-body">
                <div
                    className={`chat-v2-drop-zone${dragOver ? " is-drag-over" : ""}${isTyping ? " chat-v2-drop-zone-disabled" : ""}`}
                    onDragEnter={(e) => { if (hasFiles(e)) setDragOver(true); }}
                    onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragOver(false); }}
                    onDragOver={(e) => { if (hasFiles(e)) e.preventDefault(); }}
                    onDrop={(e) => {
                        setDragOver(false);
                        if (e.dataTransfer.files.length === 0) return;
                        e.preventDefault();
                        stageFiles(Array.from(e.dataTransfer.files));
                    }}
                >
                    <MessageList onAnswer={r => inputRef.current?.answer(r)} showToolCalls />
                </div>
                <div className={`chat-v2-settings-side-panel ${drawerMode !== null ? "open" : ""}`}>
                    <div className="chat-v2-panel-header">
                        <span className="chat-v2-panel-title">{panelTitle}</span>
                        <button className="chat-v2-panel-close-btn" onClick={handleDrawerClose} aria-label="Close panel">
                            ×
                        </button>
                    </div>
                    <div className="chat-v2-settings-side-inner">
                        <DebugPanel drawerMode={drawerMode} selectedMessage={selectedMessage} />
                    </div>
                </div>
            </div>
            <div className="chat-v2-input-area">
                <ChatInput
                    ref={inputRef}
                    onSend={sendMessage}
                    disabled={isTyping}
                    conversationEpoch={conversationEpoch}
                    attachments={attachments}
                    setAttachments={setAttachments}
                    attachmentsRef={attachmentsRef}
                    stageFiles={stageFiles}
                    handleRemove={handleRemove}
                />
            </div>
        </div>
    );
};

export default ChatInterface;
